import styled from 'styled-components';

const MOBILE_BREAKPOINT = '968px';

export const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.main};
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
`;

export const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  }
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

export const Logo = styled.img`
  height: 90px;
  width: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 70px;
  }

  @media (max-width: 480px) {
    height: 60px;
  }
`;

export const BrandName = styled.span`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: inline-block;
    margin-left: 0.6rem;
    color: #111111;
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    line-height: 1.15;
    white-space: nowrap;
  }

  @media (max-width: 360px) {
    font-size: 0.92rem;
  }
`;

export const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;

export const NavLink = styled.a`
  color: #f5f5f2;
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  position: relative;
  cursor: pointer;
  transition: color 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.accent};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    &::after {
      width: 100%;
    }
  }
`;

export const SearchIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  color: #f5f5f2;
  font-size: 1.3rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    transform: scale(1.1);
  }

  &::before {
    content: '⌕';
    font-weight: 300;
  }
`;

export const CartIconWrapper = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }
`;

export const CartIcon = styled.div`
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23f5f5f2' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z'%3E%3C/path%3E%3Cline x1='3' y1='6' x2='21' y2='6'%3E%3C/line%3E%3Cpath d='M16 10a4 4 0 0 1-8 0'%3E%3C/path%3E%3C/svg%3E");
  background-size: 70%;
  background-position: center;
  background-repeat: no-repeat;
`;

export const CartBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: ${({ theme }) => theme.colors.accent};
  color: #fff;
  font-family: 'Abel', sans-serif;
  font-size: 0.65rem;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
`;

/* ---------------- Mobile ---------------- */

export const MobileActions = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

export const HamburgerButton = styled.button<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  gap: 5px;
  z-index: 1102;

  span {
    display: block;
    width: 24px;
    height: 2px;
    background-color: #f5f5f2;
    transition: transform 0.25s ease, opacity 0.25s ease;
    transform-origin: center;
  }

  ${({ open }) =>
    open &&
    `
      span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
      span:nth-child(2) { opacity: 0; }
      span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
    `}
`;

export const MobileBackdrop = styled.div<{ open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  opacity: ${(p) => (p.open ? 1 : 0)};
  visibility: ${(p) => (p.open ? 'visible' : 'hidden')};
  transition: opacity 0.25s ease, visibility 0s linear ${(p) => (p.open ? '0s' : '0.25s')};
  z-index: 1099;

  @media (min-width: calc(${MOBILE_BREAKPOINT} + 1px)) {
    display: none;
  }
`;

export const MobileDrawer = styled.aside<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh; /* fallback for older browsers */
  height: 100dvh; /* dynamic viewport: excludes the mobile browser chrome */
  max-height: 100dvh;
  width: min(85vw, 360px);
  background: #fff;
  z-index: 1101;
  transform: translateX(${(p) => (p.open ? '0%' : '100%')});
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  padding: 6rem 1.5rem calc(2rem + env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* Keep scrolling inside the drawer so it never toggles the Android address
     bar (which caused the flicker/jump and cut off the footer). */
  overscroll-behavior: contain;

  @media (min-width: calc(${MOBILE_BREAKPOINT} + 1px)) {
    display: none;
  }
`;

export const MobileNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

export const MobileNavItem = styled.button`
  background: none;
  border: none;
  text-align: left;
  font-family: 'Abel', sans-serif;
  font-size: 1.15rem;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 1rem 0.5rem;
  cursor: pointer;
  border-bottom: 1px solid rgba(101, 116, 85, 0.1);
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    background: rgba(184, 150, 74, 0.05);
  }
`;

export const MobileFooter = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(101, 116, 85, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
